import React, { FC, useEffect, useState } from "react";
import {
  useGetSuggestionListQuery,
  useAddOrUpdateSuggestionMutation,
} from "features/api/contentManagement/contentManagementApiSlice";
import * as Yup from "yup";

import Stack from "react-bootstrap/Stack";
import { PencilSquare } from "react-bootstrap-icons";
import Form from "react-bootstrap/Form";
import { useTranslation } from "react-i18next";
import RenderList from "./List/RenderList";

import {
  Suggestion,
  suggestionTabFields,
  SuggestionFormControlsValues,
} from "../contentManagementUtils/tabSchema";
import { Button } from "./Button";
import AddContentForm from "./AddContentForm";
import { SuggestionsTabTypes } from "../contentManagementTypes";

const SuggestionsTab: FC<SuggestionsTabTypes> = () => {
  const { t } = useTranslation();
  const {
    data: suggestions,
    refetch,
  }: { data: Suggestion[]; refetch: () => void } = useGetSuggestionListQuery();
  const [addOrUpdateSuggestion] = useAddOrUpdateSuggestionMutation();

  const [showCreateForm, setShowCreateForm] = useState(false);
  const [showUpdateForm, setShowUpdateForm] = useState<Suggestion | null>(null);
  const [defaultMessageId, setDefaultMessageId] = useState<number | null>(null);

  useEffect(() => {
    if (suggestions && suggestions.length > 0) {
      const defaultItem = suggestions.find((s) => s.isDefaultRejectionMessage);
      if (defaultItem) {
        setDefaultMessageId(defaultItem.id);
      } else {
        setDefaultMessageId(null);
      }
    }
  }, [suggestions]);

  const toggleCreateForm = () => {
    setShowCreateForm(!showCreateForm);
  };

  const toggleUpdateForm = (initialValues: Suggestion | null) => {
    setShowUpdateForm(initialValues);
  };

  const suggestionsValidationSchema: Yup.ObjectSchema<SuggestionFormControlsValues> =
    Yup.object().shape({
      title: Yup.string().required(t("חובה להזין כותרת")),
      text: Yup.string().required(t("חובה להזין הצעה")),
    });

  const suggestionsInitialValues: Suggestion = {
    id: 0,
    title: "",
    text: "",
    disable: false,
    isDefaultRejectionMessage: false,
  };

  const handleCreateOrUpdateSuggestion: (values: Suggestion) => void = async (
    values: Suggestion
  ) => {
    const payload = {
      id: values.id ?? 0,
      title: values.title,
      text: values.text,
      disable: values.disable,
      isDefaultRejectionMessage: values.isDefaultRejectionMessage,
    };

    await addOrUpdateSuggestion(payload).unwrap();

    refetch();

    values.id ? toggleUpdateForm(null) : toggleCreateForm();
  };

  const handleDisable = async (suggestion: Suggestion) => {
    const updatedSuggestion = {
      ...suggestion,
      disable: !suggestion.disable,
    };
    await addOrUpdateSuggestion(updatedSuggestion).unwrap();
    refetch();
  };

  const handleSetDefaultMessage = async (suggestion: Suggestion) => {
    const updatedSuggestion = {
      ...suggestion,
      isDefaultRejectionMessage: !suggestion.isDefaultRejectionMessage,
    };
    await addOrUpdateSuggestion(updatedSuggestion).unwrap();
    refetch();
  };

  const columns = [
    {
      title: t("קוד"),
      dataIndex: "id",
      key: "id",
      flex: 1,
      renderCell: (text: string) => <span>{text}</span>,
      asc: true,
      minWidth: 150,
    },
    {
      title: t("כותרת"),
      dataIndex: "title",
      key: "title",
      flex: 1,
      renderCell: (text: string) => <span>{text}</span>,
      asc: true,
      minWidth: 150,
    },
    {
      title: t("הצעה"),
      dataIndex: "text",
      key: "text",
      flex: 1,
      renderCell: (text: string) => <span>{text}</span>,
      asc: true,
      minWidth: 150,
    },
    {
      title: t("חסום?"),
      dataIndex: "disable",
      key: "actions",
      flex: 1,
      renderCell: (suggestion: Suggestion) => {
        return (
          <Stack direction="horizontal" gap={0}>
            <Form.Check
              type="switch"
              id={`custom-switch-${suggestion.id}`}
              checked={suggestion.disable}
              onChange={() => handleDisable(suggestion)}
              disabled={defaultMessageId && defaultMessageId === suggestion.id}
              size={20}
            />
          </Stack>
        );
      },
      asc: true,
      minWidth: 150,
    },
    {
      title: t("הודעה דיפולטית - ללוחית רישוי לא ברורה"),
      dataIndex: "defaultMessage",
      key: "actions",
      flex: 1,
      renderCell: (suggestion: Suggestion) => {
        return (
          <Stack direction="horizontal" gap={0}>
            <Form.Check
              type="switch"
              id={`custom-switch-${suggestion.id}`}
              checked={suggestion.isDefaultRejectionMessage}
              disabled={
                suggestion.disable ||
                (defaultMessageId && defaultMessageId !== suggestion.id)
              }
              onChange={() => handleSetDefaultMessage(suggestion)}
              size={20}
            />
          </Stack>
        );
      },
      asc: true,
      minWidth: 150,
    },
    {
      title: "",
      dataIndex: "actions",
      key: "actions",
      flex: 1,
      renderCell: (suggestion: Suggestion) => {
        return (
          <Stack direction="horizontal" gap={0}>
            <PencilSquare
              size={20}
              color="primary"
              onClick={() => {
                toggleUpdateForm(suggestion);
              }}
            />
          </Stack>
        );
      },
      asc: true,
      minWidth: 150,
    },
  ];

  return (
    <div className="tab-list-container">
      <div className="tab-list-header">
        <Button title={t("add")} handleClick={toggleCreateForm} />
      </div>
      <RenderList<Suggestion>
        data={suggestions ? [...suggestions] : []}
        columns={columns}
        disableField="disable"
        isDraggable
      />
      <AddContentForm
        isOpen={showCreateForm}
        toggle={toggleCreateForm}
        fields={suggestionTabFields}
        submitForm={handleCreateOrUpdateSuggestion}
        headerTitle={t("הצעה חדשה")}
        approveBtnLabel={t("יצירת הצעה")}
        cancelBtnLabel={t("cancel")}
        initialValues={suggestionsInitialValues}
        validationSchema={suggestionsValidationSchema}
      />
      <AddContentForm
        isOpen={!!showUpdateForm}
        toggle={toggleUpdateForm}
        fields={suggestionTabFields}
        submitForm={handleCreateOrUpdateSuggestion}
        headerTitle={t("עדכון הצעה")}
        approveBtnLabel={t("עדכן הצעה")}
        cancelBtnLabel={t("cancel")}
        initialValues={showUpdateForm}
        validationSchema={suggestionsValidationSchema}
      />
    </div>
  );
};

export default SuggestionsTab;
